import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Plane, 
  Search, 
  Loader2, 
  MapPin, 
  Clock, 
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Navigation
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons
const createAirportIcon = (color) => L.divIcon({
  className: 'custom-airport-icon',
  html: `<div style="
    width: 24px; 
    height: 24px; 
    background: ${color}; 
    border-radius: 50%; 
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const createPlaneIcon = (heading = 0) => L.divIcon({
  className: 'custom-plane-icon',
  html: `<div style="
    width: 40px; 
    height: 40px; 
    display: flex;
    align-items: center;
    justify-content: center;
    transform: rotate(${heading}deg);
  ">
    <svg width="32" height="32" viewBox="0 0 24 24" fill="#3B82F6" stroke="white" stroke-width="1">
      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

// Map center updater component
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom, { duration: 1.5 });
    }
  }, [center, zoom, map]);
  return null;
};

// Demo flight data
const DEMO_FLIGHTS = {
  'EK123': {
    flightNumber: 'EK123',
    airline: 'Emirates',
    aircraft: 'Boeing 777-300ER',
    status: 'in_flight',
    departure: {
      airport: 'DXB',
      city: 'Dubai',
      country: 'UAE',
      lat: 25.2532,
      lng: 55.3657,
      scheduled: '08:00',
      actual: '08:15',
      terminal: '3',
      gate: 'A1'
    },
    arrival: {
      airport: 'LHR',
      city: 'London',
      country: 'UK',
      lat: 51.4700,
      lng: -0.4543,
      scheduled: '12:30',
      estimated: '12:45',
      terminal: '3',
      gate: 'B22'
    },
    position: {
      lat: 40.5,
      lng: 25.0,
      altitude: 38000,
      speed: 920,
      heading: 315
    },
    progress: 65,
    delay: 15
  },
  'QR456': {
    flightNumber: 'QR456',
    airline: 'Qatar Airways',
    aircraft: 'Airbus A350-1000',
    status: 'in_flight',
    departure: {
      airport: 'DOH',
      city: 'Doha',
      country: 'Qatar',
      lat: 25.2731,
      lng: 51.6081,
      scheduled: '14:00',
      actual: '14:05',
      terminal: '1',
      gate: 'C15'
    },
    arrival: {
      airport: 'JFK',
      city: 'New York',
      country: 'USA',
      lat: 40.6413,
      lng: -73.7781,
      scheduled: '21:00',
      estimated: '21:10',
      terminal: '8',
      gate: 'A5'
    },
    position: {
      lat: 45.0,
      lng: -30.0,
      altitude: 41000,
      speed: 890,
      heading: 280
    },
    progress: 55,
    delay: 10
  },
  'SV101': {
    flightNumber: 'SV101',
    airline: 'Saudia',
    aircraft: 'Boeing 787-9',
    status: 'landed',
    departure: {
      airport: 'JED',
      city: 'Jeddah',
      country: 'Saudi Arabia',
      lat: 21.6796,
      lng: 39.1566,
      scheduled: '06:00',
      actual: '06:10',
      terminal: '1',
      gate: 'D8'
    },
    arrival: {
      airport: 'CAI',
      city: 'Cairo',
      country: 'Egypt',
      lat: 30.1219,
      lng: 31.4056,
      scheduled: '08:30',
      actual: '08:25',
      terminal: '3',
      gate: 'E12'
    },
    position: {
      lat: 30.1219,
      lng: 31.4056,
      altitude: 0,
      speed: 0,
      heading: 0
    },
    progress: 100,
    delay: -5
  },
  'EY789': {
    flightNumber: 'EY789',
    airline: 'Etihad Airways',
    aircraft: 'Airbus A380-800',
    status: 'delayed',
    departure: {
      airport: 'AUH',
      city: 'Abu Dhabi',
      country: 'UAE',
      lat: 24.4330,
      lng: 54.6511,
      scheduled: '10:00',
      actual: null,
      terminal: '1',
      gate: 'F3'
    },
    arrival: {
      airport: 'SYD',
      city: 'Sydney',
      country: 'Australia',
      lat: -33.9399,
      lng: 151.1753,
      scheduled: '06:30',
      estimated: '07:15',
      terminal: '1',
      gate: 'TBD'
    },
    position: {
      lat: 24.4330,
      lng: 54.6511,
      altitude: 0,
      speed: 0,
      heading: 120
    },
    progress: 0,
    delay: 45
  }
};

const FlightTrackingMap = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [flight, setFlight] = useState(null);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState([25.2, 45.0]);
  const [mapZoom, setMapZoom] = useState(3);
  const [isRealData, setIsRealData] = useState(false);

  const searchFlight = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) {
      setError('الرجاء إدخال رقم الرحلة / Please enter flight number');
      return;
    }

    setLoading(true);
    setError(null);
    setFlight(null);
    setIsRealData(false);

    try {
      // Try real API first
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/flights/search/${searchQuery.toUpperCase().replace(/\s/g, '')}`);
      const data = await response.json();
      
      if (data.error) {
        // Fall back to demo data
        const flightKey = searchQuery.toUpperCase().replace(/\s/g, '');
        const demoFlight = DEMO_FLIGHTS[flightKey];
        
        if (demoFlight) {
          setFlight(demoFlight);
          setMapCenter([demoFlight.position.lat, demoFlight.position.lng]);
          setMapZoom(4);
          setIsRealData(false);
        } else {
          setError(`لم يتم العثور على الرحلة ${searchQuery} / Flight not found`);
        }
      } else {
        // Use real API data
        setFlight(data);
        setIsRealData(true);
        
        // Set map center based on flight status
        if (data.position && data.position.lat && data.position.lng) {
          setMapCenter([data.position.lat, data.position.lng]);
        } else if (data.status === 'landed' && data.arrival.lat) {
          setMapCenter([data.arrival.lat, data.arrival.lng]);
        } else if (data.departure.lat) {
          setMapCenter([data.departure.lat, data.departure.lng]);
        }
        setMapZoom(4);
      }
    } catch (err) {
      console.error('Flight search error:', err);
      // Fall back to demo data on error
      const flightKey = searchQuery.toUpperCase().replace(/\s/g, '');
      const demoFlight = DEMO_FLIGHTS[flightKey];
      
      if (demoFlight) {
        setFlight(demoFlight);
        setMapCenter([demoFlight.position.lat, demoFlight.position.lng]);
        setMapZoom(4);
        setIsRealData(false);
      } else {
        setError(`خطأ في البحث / Search error: ${err.message}`);
      }
    }

    setLoading(false);
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'in_flight':
        return { color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'في الجو / IN FLIGHT', icon: Plane };
      case 'landed':
        return { color: 'text-emerald-400', bg: 'bg-emerald-500/20', label: 'وصلت / LANDED', icon: CheckCircle };
      case 'delayed':
        return { color: 'text-amber-400', bg: 'bg-amber-500/20', label: 'متأخرة / DELAYED', icon: AlertTriangle };
      default:
        return { color: 'text-gray-400', bg: 'bg-gray-500/20', label: status, icon: Plane };
    }
  };

  // Safe position getter
  const getFlightPosition = () => {
    if (!flight) return null;
    if (flight.position && flight.position.lat && flight.position.lng) {
      return [flight.position.lat, flight.position.lng];
    }
    // For flights not yet departed, use departure airport
    if (flight.departure && flight.departure.lat && flight.departure.lng) {
      return [flight.departure.lat, flight.departure.lng];
    }
    return null;
  };

  const flightPath = flight && flight.departure?.lat && getFlightPosition() ? [
    [flight.departure.lat, flight.departure.lng],
    getFlightPosition()
  ] : [];

  const remainingPath = flight && getFlightPosition() && flight.arrival?.lat ? [
    getFlightPosition(),
    [flight.arrival.lat, flight.arrival.lng]
  ] : [];

  return (
    <div className="space-y-4">
      {/* Search Section */}
      <Card className="bg-gradient-to-br from-[#1E293B] to-[#0F172A] border-white/10">
        <CardContent className="p-4">
          <form onSubmit={searchFlight} className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Navigation className="h-5 w-5 text-blue-400" />
              <h3 className="text-white font-bold">تتبع الرحلة / Track Flight</h3>
            </div>
            
            <div className="flex gap-2">
              <Input
                placeholder="رقم الرحلة / Flight Number (e.g., EK123)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                className="bg-[#0F172A] border-white/20 text-white placeholder:text-white/40"
              />
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-500 min-w-[100px]"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-1" />
                    بحث
                  </>
                )}
              </Button>
            </div>

            {/* Demo flights hint */}
            <div className="flex flex-wrap gap-2 mt-2">
              <span className="text-white/40 text-xs">جرب / Try:</span>
              {Object.keys(DEMO_FLIGHTS).map(fn => (
                <button
                  key={fn}
                  type="button"
                  onClick={() => { setSearchQuery(fn); }}
                  className="text-xs px-2 py-1 bg-white/5 hover:bg-white/10 rounded text-blue-400 transition-colors"
                >
                  {fn}
                </button>
              ))}
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Flight Info & Map */}
      <AnimatePresence>
        {flight && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            {/* Flight Status Card */}
            <Card className="bg-gradient-to-br from-[#1E293B] to-[#0F172A] border-white/10 overflow-hidden">
              <CardContent className="p-0">
                {/* Header */}
                <div className={`p-4 ${getStatusConfig(flight.status).bg}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                        <Plane className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-white font-bold text-xl">{flight.flightNumber}</h2>
                        <p className="text-white/70 text-sm">{flight.airline} • {flight.aircraft}</p>
                      </div>
                    </div>
                    <div className={`px-3 py-1.5 rounded-full ${getStatusConfig(flight.status).bg} border border-white/20`}>
                      <span className={`text-sm font-bold ${getStatusConfig(flight.status).color}`}>
                        {getStatusConfig(flight.status).label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Route Info */}
                <div className="p-4 grid grid-cols-3 gap-2">
                  {/* Departure */}
                  <div className="text-center">
                    <div className="w-10 h-10 mx-auto bg-emerald-500/20 rounded-full flex items-center justify-center mb-2">
                      <div className="w-4 h-4 bg-emerald-500 rounded-full" />
                    </div>
                    <p className="text-2xl font-bold text-white">{flight.departure.airport}</p>
                    <p className="text-white/60 text-xs">{flight.departure.city}</p>
                    <p className="text-emerald-400 text-sm font-mono mt-1">
                      {flight.departure.actual || flight.departure.scheduled}
                    </p>
                    {flight.departure.terminal && (
                      <p className="text-white/40 text-xs">T{flight.departure.terminal} • Gate {flight.departure.gate}</p>
                    )}
                  </div>

                  {/* Progress */}
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-full flex items-center gap-1">
                      <div className="flex-1 h-1 bg-emerald-500 rounded-l" style={{ width: `${flight.progress}%` }} />
                      <Plane className="h-4 w-4 text-blue-400 transform -rotate-90" />
                      <div className="flex-1 h-1 bg-white/20 rounded-r" style={{ width: `${100 - flight.progress}%` }} />
                    </div>
                    <p className="text-white/60 text-xs mt-2">{flight.progress}%</p>
                    {flight.delay !== 0 && (
                      <p className={`text-xs mt-1 ${flight.delay > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {flight.delay > 0 ? `+${flight.delay} دقيقة تأخير` : `${Math.abs(flight.delay)} دقيقة مبكراً`}
                      </p>
                    )}
                  </div>

                  {/* Arrival */}
                  <div className="text-center">
                    <div className="w-10 h-10 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-2">
                      <div className="w-4 h-4 bg-red-500 rounded-full" />
                    </div>
                    <p className="text-2xl font-bold text-white">{flight.arrival.airport}</p>
                    <p className="text-white/60 text-xs">{flight.arrival.city}</p>
                    <p className="text-blue-400 text-sm font-mono mt-1">
                      {flight.arrival.estimated || flight.arrival.scheduled}
                    </p>
                    {flight.arrival.terminal && (
                      <p className="text-white/40 text-xs">T{flight.arrival.terminal} • Gate {flight.arrival.gate}</p>
                    )}
                  </div>
                </div>

                {/* Flight Stats */}
                {flight.status === 'in_flight' && (
                  <div className="px-4 pb-4 grid grid-cols-3 gap-2">
                    <div className="bg-white/5 rounded-lg p-3 text-center">
                      <p className="text-white/40 text-xs">الارتفاع / Altitude</p>
                      <p className="text-white font-bold">{flight.position.altitude.toLocaleString()} ft</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 text-center">
                      <p className="text-white/40 text-xs">السرعة / Speed</p>
                      <p className="text-white font-bold">{flight.position.speed} km/h</p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3 text-center">
                      <p className="text-white/40 text-xs">الاتجاه / Heading</p>
                      <p className="text-white font-bold">{flight.position.heading}°</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Map */}
            <Card className="bg-gradient-to-br from-[#1E293B] to-[#0F172A] border-white/10 overflow-hidden">
              <CardContent className="p-0">
                <div className="h-[400px] w-full">
                  <MapContainer
                    center={mapCenter}
                    zoom={mapZoom}
                    className="h-full w-full"
                    style={{ background: '#0F172A' }}
                  >
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    
                    <MapUpdater center={mapCenter} zoom={mapZoom} />

                    {/* Departure Airport */}
                    {flight.departure?.lat && flight.departure?.lng && (
                      <Marker 
                        position={[flight.departure.lat, flight.departure.lng]}
                        icon={createAirportIcon('#22C55E')}
                      >
                        <Popup className="custom-popup">
                          <div className="text-center p-2">
                            <p className="font-bold text-lg">{flight.departure.airport}</p>
                            <p className="text-sm text-gray-600">{flight.departure.city}, {flight.departure.country}</p>
                            <p className="text-emerald-600 font-mono">مغادرة / Departure</p>
                          </div>
                        </Popup>
                      </Marker>
                    )}

                    {/* Arrival Airport */}
                    {flight.arrival?.lat && flight.arrival?.lng && (
                      <Marker 
                        position={[flight.arrival.lat, flight.arrival.lng]}
                        icon={createAirportIcon('#EF4444')}
                      >
                        <Popup>
                          <div className="text-center p-2">
                            <p className="font-bold text-lg">{flight.arrival.airport}</p>
                            <p className="text-sm text-gray-600">{flight.arrival.city}, {flight.arrival.country}</p>
                            <p className="text-red-600 font-mono">وصول / Arrival</p>
                          </div>
                        </Popup>
                      </Marker>
                    )}

                    {/* Aircraft Position */}
                    {flight.status === 'in_flight' && flight.position?.lat && flight.position?.lng && (
                      <Marker 
                        position={[flight.position.lat, flight.position.lng]}
                        icon={createPlaneIcon(flight.position.heading || 0)}
                      >
                        <Popup>
                          <div className="text-center p-2">
                            <p className="font-bold text-lg">{flight.flightNumber}</p>
                            <p className="text-sm">Alt: {(flight.position.altitude || 0).toLocaleString()} ft</p>
                            <p className="text-sm">Speed: {flight.position.speed || 0} km/h</p>
                          </div>
                        </Popup>
                      </Marker>
                    )}

                    {/* Flight Path - Completed */}
                    {flightPath.length === 2 && (
                      <Polyline
                        positions={flightPath}
                        pathOptions={{ color: '#22C55E', weight: 3, opacity: 0.8 }}
                      />
                    )}

                    {/* Flight Path - Remaining */}
                    {remainingPath.length === 2 && (
                      <Polyline
                        positions={remainingPath}
                        pathOptions={{ color: '#3B82F6', weight: 3, opacity: 0.5, dashArray: '10, 10' }}
                      />
                    )}
                  </MapContainer>
                </div>

                {/* Map Legend */}
                <div className="p-3 bg-black/30 flex items-center justify-center gap-6 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                    <span className="text-white/60">مطار المغادرة</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full" />
                    <span className="text-white/60">مطار الوصول</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Plane className="w-3 h-3 text-blue-400" />
                    <span className="text-white/60">موقع الطائرة</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Source Notice */}
            <div className={`border rounded-xl p-3 text-center ${
              isRealData 
                ? 'bg-emerald-500/10 border-emerald-500/30' 
                : 'bg-amber-500/10 border-amber-500/30'
            }`}>
              <p className={`text-xs ${isRealData ? 'text-emerald-400' : 'text-amber-400'}`}>
                {isRealData 
                  ? '✅ بيانات حقيقية من AeroDataBox / Real-time data from AeroDataBox'
                  : '⚠️ وضع العرض التجريبي / Demo Mode - البيانات تجريبية وليست حقيقية'
                }
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FlightTrackingMap;
